import { createContext, useCallback, useContext, useState } from "react";
import { useNavigate } from "react-router";
import { useLocalization } from "@fluent/react";
import { useNotificationContext } from "cerulean";
import { useOrganizationUserContext } from "byzantine/src/OrganizationUser/OrganizationUserProvider";
import InstitutionSettingsContext from "../contexts/InstitutionSettingsContext";
import useSudoApiHandler from "../SudoContext/useSudoApiHandler";
import NewUserInfo, { NewUserFormData } from "./NewUserInfo";
import NewUserRoleSelection from "./NewUserRoleSelection";

interface NewUserFlowContextType {
  formData: NewUserFormData;
  isSubmitting: boolean;
  currentStep: "info" | "roleSelection";
  onNext: (data: NewUserFormData) => void;
  onSubmit: (data: NewUserFormData & { role_uuid: string }) => void;
  onCancel: () => void;
  allAccounts?: (API.Account | API.ExternalAccount)[];
  orgLimits?: API.Limits.ByType;
  hasInstitutionLoaded?: boolean;
}

const NewUserFlowContext = createContext<NewUserFlowContextType | null>(null);

export function useNewUserFlowContext() {
  const context = useContext(NewUserFlowContext);
  if (!context) {
    throw new Error("useNewUserFlowContext must be used within NewUserFlow");
  }
  return context;
}

interface NewUserFlowProps {
  organizationUuid: UUID;
  orgId?: string;
  allAccounts?: (API.Account | API.ExternalAccount)[];
  orgLimits?: API.Limits.ByType;
  hasInstitutionLoaded?: boolean;
}

export function NewUserFlow({
  organizationUuid,
  allAccounts = [],
}: NewUserFlowProps) {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const institution = useContext(InstitutionSettingsContext);
  const orgLimits = institution?.features?.limits || {};
  const hasInstitutionLoaded = !!institution?.features?.limits;
  const navigate = useNavigate();
  const { createOrganizationUser } = useOrganizationUserContext();
  const handleSudoRequest = useSudoApiHandler();

  const [currentStep, setCurrentStep] = useState<"info" | "roleSelection">(
    "info"
  );

  const [formData, setFormData] = useState<NewUserFormData>({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNext = useCallback((data: NewUserFormData) => {
    setFormData(data);
    setCurrentStep("roleSelection");
  }, []);

  const handleSubmit = useCallback(
    async (data: NewUserFormData & { role_uuid: string }) => {
      setIsSubmitting(true);

      try {
        const payload: API.CreateOrganizationUser.Request = {
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          phone: data.phone,
          role_uuid: data.role_uuid,
        };

        await handleSudoRequest(() =>
          createOrganizationUser(payload)
        );

        sendNotification({
          type: "success",
          text: l10n.getString("new-user-success-message", {
            email: data.email,
          }),
        });

        navigate("/");
      } catch (error) {
        sendNotification({
          type: "negative",
          text:
            error instanceof Error
              ? error.message
              : l10n.getString("new-user-error-message"),
        });
        setIsSubmitting(false);
      }
    },
    [organizationUuid, sendNotification, l10n, handleSudoRequest]
  );

  const handleCancel = useCallback(() => {
    navigate("/");
  }, []);

  const contextValue: NewUserFlowContextType = {
    formData,
    isSubmitting,
    currentStep,
    onNext: handleNext,
    onSubmit: handleSubmit,
    onCancel: handleCancel,
    allAccounts,
    orgLimits,
    hasInstitutionLoaded,
  };

  return (
    <NewUserFlowContext.Provider value={contextValue}>
      {currentStep === "info" ? (
        <NewUserInfo
          initialData={formData}
          onNext={handleNext}
          onCancel={handleCancel}
          isPending={isSubmitting}
        />
      ) : (
        <NewUserRoleSelection
          formData={formData}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isPending={isSubmitting}
          allAccounts={allAccounts}
          orgLimits={orgLimits}
          hasInstitutionLoaded={hasInstitutionLoaded}
        />
      )}
    </NewUserFlowContext.Provider>
  );
}
export default NewUserFlow;
